import { memo, useEffect, useMemo, useState } from 'react'
import { CalendarRange, Search } from 'lucide-react'
import { itemsApi, programsApi } from '../../lib/api.js'
import { formatDuration, isBroadcastableMedia, weekMondayISO } from '../../lib/utils.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import type {
  Program,
  ReplaceProgramRangeResult,
  ReplaceProgramScope,
  ScheduleItem,
} from '../../types/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import './AgendaDialogs.css'

function localDateInput(iso: string): string {
  const value = new Date(iso)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function addLocalDays(dateInput: string, days: number): string {
  const [year, month, day] = dateInput.split('-').map(Number)
  const value = new Date(year, month - 1, day + days)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function hasUsableMedia(program: Program): boolean {
  return Boolean(
    program.programCode ||
    program.mediaAssets?.some(asset => !asset.isProxy && isBroadcastableMedia(asset.status)),
  )
}

function scopeLabels(item: ScheduleItem): Record<Exclude<ReplaceProgramScope, 'UNTIL'>, string> {
  const date = new Date(item.startTime)
  const nextDay = new Date(date)
  nextDay.setDate(date.getDate() + 1)
  const monday = new Date(date)
  monday.setDate(date.getDate() - ((date.getDay() + 6) % 7))
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const day = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  const weekStart = monday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  const weekEnd = sunday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  const month = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  return {
    DAY: `Sur la journée du ${day}`,
    NEXT_DAY: `Sur cette journée et le lendemain (${nextDay.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })})`,
    WEEK: `Sur la semaine du ${weekStart} au ${weekEnd}`,
    MONTH: `Sur le mois de ${month}`,
  }
}

export const ReplaceProgramDialog = memo(function ReplaceProgramDialog(props: {
  item: ScheduleItem
  channelId: string
  onSaved: (result: ReplaceProgramRangeResult) => void
  onClose: () => void
}) {
  const sourceDate = useMemo(() => localDateInput(props.item.startTime), [props.item.startTime])
  const nextDate = useMemo(() => addLocalDays(sourceDate, 1), [sourceDate])
  const labels = useMemo(() => scopeLabels(props.item), [props.item])
  const [query, setQuery] = useState('')
  const [programs, setPrograms] = useState<Program[]>([])
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null)
  const [scope, setScope] = useState<ReplaceProgramScope>('DAY')
  const [untilDate, setUntilDate] = useState(nextDate)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoading(true)
      programsApi.list({
        channelId: props.channelId,
        search: query.trim() || undefined,
        limit: 100,
        sortBy: 'title',
        sortDir: 'asc',
      }).then(result => {
        if (cancelled) return
        setPrograms((result.data ?? []).filter((program: Program) => (
          program.id !== props.item.programId &&
          program.type === 'EMISSION' &&
          !program.isLive
        )))
      }).catch(cause => {
        if (!cancelled) setError(cause?.message ?? 'Impossible de charger les programmes.')
      }).finally(() => {
        if (!cancelled) setLoading(false)
      })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [props.channelId, props.item.programId, query])

  const replace = async () => {
    if (!selectedProgram) return setError('Sélectionnez le programme de remplacement.')
    if (scope === 'UNTIL' && (!untilDate || untilDate < sourceDate)) {
      return setError('La date de fin doit être égale ou postérieure à la diffusion sélectionnée.')
    }
    setBusy(true)
    setError(null)
    try {
      // Le remplacement peut porter au-delà de la semaine photographiée : sur un
      // mois, jusqu'à une date lointaine, ou jusqu'au lundi suivant depuis un
      // dimanche, il traverse plusieurs grilles
      // hebdomadaires dont on ignore les identifiants avant de lancer l'action.
      // Une photo n'en couvrirait qu'une partie, et l'annulation ne défaudrait
      // que cette partie-là sans le dire. Dans ces cas on préfère ne rien
      // proposer : l'opérateur garde l'historique de génération pour revenir en
      // arrière sur une portée aussi large.
      const recordable = scope === 'DAY' || scope === 'WEEK'
        || (scope === 'NEXT_DAY' && weekMondayISO(nextDate) === weekMondayISO(sourceDate))
        || (scope === 'UNTIL' && weekMondayISO(untilDate) === weekMondayISO(broadcastDayKey(props.item.startTime)))

      const run = () => itemsApi.replaceProgramRange(props.item.id, {
        programId: selectedProgram.id,
        scope,
        ...(scope === 'UNTIL' ? { untilDate } : {}),
      })
      const result = recordable
        ? await recordGridChange({
            label:   `remplacement de ${props.item.program?.title ?? 'ce programme'}`,
            gridIds: [props.item.gridId],
            date:    broadcastDayKey(props.item.startTime),
            gridIdsFromResult: (applied: ReplaceProgramRangeResult) => applied.gridIds,
          }, run)
        : await run()
      props.onSaved(result)
      props.onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible de remplacer ce programme.')
    } finally {
      setBusy(false)
    }
  }

  const durationChanged = selectedProgram && selectedProgram.durationSec !== props.item.durationSec

  return (
    <ModalShell title="Remplacement d’un programme" onClose={props.onClose} closeDisabled={busy} size="medium" className="agenda-dialog agenda-dialog--replace">
      <div className="agenda-dialog__subtitle agenda-dialog__subtitle--ellipsis">Remplacer « {props.item.program?.title ?? 'Programme'} »</div>

      <div className="agenda-replace__body">
        <section>
          <div className="agenda-dialog__section-label">Par le programme</div>
          <div className="agenda-replace__search-wrap">
            <Search size={14} aria-hidden="true" className="agenda-replace__search-icon" />
            <input
              autoFocus
              className="tp-input agenda-replace__search"
              placeholder="Rechercher un titre…"
              value={query}
              onChange={event => {
                setQuery(event.target.value)
                setSelectedProgram(null)
              }}
            />
          </div>
          <div className="agenda-replace__program-list">
            {loading && <div className="agenda-dialog__empty"><Spinner /> Chargement…</div>}
            {!loading && programs.length === 0 && <div className="agenda-dialog__empty">Aucun programme trouvé.</div>}
            {!loading && programs.map(program => {
              const usable = hasUsableMedia(program)
              const selected = selectedProgram?.id === program.id
              return <button
                key={program.id}
                type="button"
                disabled={busy}
                aria-pressed={selected}
                onClick={() => setSelectedProgram(program)}
                className={`agenda-replace__program ${selected ? 'is-selected' : ''}`}
              >
                <span className="agenda-replace__program-main">
                  <span className="agenda-replace__program-title">{program.title}</span>
                  <span className={`agenda-replace__program-meta ${usable ? '' : 'is-warning'}`}>
                    {usable ? (program.genre ?? 'Programme') : 'Média indisponible'}
                  </span>
                </span>
                <span className="agenda-replace__program-duration">{formatDuration(program.durationSec)}</span>
              </button>
            })}
          </div>
        </section>

        <fieldset className="agenda-replace__scope">
          <legend className="agenda-dialog__section-label agenda-dialog__section-label--icon">
            <CalendarRange size={13} aria-hidden="true" /> Portée du remplacement
          </legend>
          <p className="agenda-replace__scope-description">
            Toutes les occurrences encore planifiées de « {props.item.program?.title ?? 'ce programme'} » dans la période choisie seront remplacées.
          </p>
          <div className="agenda-replace__scope-options">
            {(['DAY', 'NEXT_DAY', 'WEEK', 'MONTH'] as const).map(value => <label key={value} className="agenda-replace__scope-option">
              <input type="radio" name="replacement-scope" checked={scope === value} onChange={() => setScope(value)} />
              <span>{labels[value]}</span>
            </label>)}
            <label className="agenda-replace__scope-until">
              <input type="radio" name="replacement-scope" checked={scope === 'UNTIL'} onChange={() => setScope('UNTIL')} />
              <span>Jusqu’au</span>
              <input
                type="date"
                min={sourceDate}
                className="tp-input"
                value={untilDate}
                onFocus={() => setScope('UNTIL')}
                onChange={event => { setUntilDate(event.target.value); setScope('UNTIL') }}
              />
            </label>
          </div>
        </fieldset>

        {durationChanged && <div className="agenda-dialog__notice is-warning">
          La durée passera de {formatDuration(props.item.durationSec)} à {formatDuration(selectedProgram.durationSec)}. L’habillage et les écrans publicitaires collés au programme le suivront, et la plage d’infoservice sera allongée ou réduite d’autant. Le programme ou direct suivant ne bouge pas.
        </div>}
        {error && <div role="alert" className="agenda-dialog__error">{error}</div>}
      </div>

      <footer className="tp-modal-actions agenda-replace__actions">
        <button className="tp-button" disabled={busy} onClick={props.onClose}>Annuler</button>
        <button className="tp-button tp-button--primary" disabled={!selectedProgram || busy} data-state={busy ? 'loading' : undefined} onClick={() => { replace().catch(console.error) }}>
          {busy ? 'Remplacement…' : 'Remplacer'}
        </button>
      </footer>
    </ModalShell>
  )
})
